import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { DollarSign, Loader2, CreditCard } from 'lucide-react';

export default function PendingIncentives() {
  const navigate = useNavigate();
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payId, setPayId] = useState(null);
  const [txnRef, setTxnRef] = useState('');
  const [paymentMode, setPaymentMode] = useState('UPI');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await adminAPI.getPendingIncentives(); setIncentives(res.data.data || []); }
    catch { toast.error('Failed to load incentives'); }
    finally { setLoading(false); }
  };

  const handlePay = async () => {
    if (!txnRef.trim()) { toast.error('Transaction reference is required'); return; }
    setActionLoading(true);
    try {
      await adminAPI.markIncentivePaid(payId, { transactionReference: txnRef, paymentMode });
      toast.success('Incentive marked as paid!');
      setIncentives(incentives.filter(i => i.id !== payId));
      setPayId(null); setTxnRef('');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="h-10 w-10 animate-spin text-teal-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav title="Pending Incentives" subtitle={incentives.length + " payments pending"} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {incentives.length === 0 ? (
          <div className="text-center py-20">
            <DollarSign className="h-16 w-16 text-green-200 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-500">No pending payments</h3>
          </div>
        ) : (
          <div className="space-y-4">
            {incentives.map((inc) => (
              <div key={inc.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-lg">₹{inc.amount}</p>
                      <p className="text-sm text-gray-500">{inc.incentiveType?.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-gray-700 mt-1">
                        Pay to: <span className="font-semibold">{inc.recipientName}</span>
                        <span className="text-gray-400 ml-2 text-xs">({inc.recipientPhone})</span>
                      </p>
                      {inc.description && <p className="text-xs text-gray-400 mt-0.5">{inc.description}</p>}
                    </div>
                  </div>
                  <button onClick={() => setPayId(inc.id)}
                    className="flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition shadow-lg shadow-green-500/20">
                    <CreditCard className="h-4 w-4" /> Mark as Paid
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {payId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="font-bold text-slate-800 mb-5">Record Payment</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Payment Mode</label>
                  <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CASH">Cash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Transaction Reference *</label>
                  <input type="text" value={txnRef} onChange={(e) => setTxnRef(e.target.value)}
                    placeholder="e.g., UPI/2026/123456"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setPayId(null); setTxnRef(''); }}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={handlePay} disabled={actionLoading}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-xl font-bold text-sm hover:bg-green-700 disabled:opacity-50">
                  {actionLoading ? 'Processing...' : 'Confirm Payment'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}